import {
  DebouncedSearchInput,
  Modal,
  ModalActionButtonProps,
  useRunWithFlag,
} from "@msys/ui";
import { Stack } from "@mui/material";
import { SimpleTreeView } from "@mui/x-tree-view";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { CategoryTreeItems } from "./CategoryTreeItems.js";
import { buildCategoryTree, filterCategories } from "./helpers.js";
import type { CategoryFragment } from "./useCategories.generated.js";
import { useCategories } from "./useCategories.js";
import { useCategoriesExpandedItems } from "./useCategoriesExpandedItems.js";

interface Props {
  title?: string;
  handleClose: () => void;
  handleSelectCategory: (
    category: CategoryFragment,
    handleClose: () => void
  ) => Promise<void> | void;
  handleClearCategory?: (handleClose: () => void) => Promise<void> | void;
  onCategoryClick?: (category: CategoryFragment) => void;
  excludeCategoryIds?: string[];
  initialSelectedCategoryId?: string | null;
}

export const CategorySearchModal = ({
  title,
  handleClose,
  handleSelectCategory,
  handleClearCategory,
  onCategoryClick,
  excludeCategoryIds,
  initialSelectedCategoryId,
}: Props) => {
  const { t } = useTranslate(["Categories", "Global"]);

  const [selectedCategoryId, setSelectedCategoryId] = React.useState<
    string | null
  >(initialSelectedCategoryId ?? null);

  const [processingSelect, runSelect] = useRunWithFlag();
  const [processingClear, runClear] = useRunWithFlag();
  const processing = processingSelect || processingClear;

  const { getCategories, loading } = useCategories();
  const categories = React.useMemo(
    () =>
      getCategories({
        addParentsWhenNotIncluded: false,
        removeChildrenIfIncluded: false,
      }),
    [getCategories]
  );

  // Remove selected category ID that are not in the loaded categories
  React.useEffect(() => {
    if (categories.length > 0) {
      const selectedCategory = categories.find(
        c => c.id === selectedCategoryId
      );
      if (!selectedCategory) {
        setSelectedCategoryId(null);
      }
    }
  }, [categories, selectedCategoryId]);

  return (
    <Modal
      maxWidth="sm"
      title={title ?? t("Select category", { ns: "Categories" })}
      actionButtons={
        [
          {
            label: t("Cancel", { ns: "Global" }),
            handleClick: handleClose,
            buttonProps: { variant: "text", disabled: processing },
          },
          ...(handleClearCategory
            ? ([
                {
                  label: t("Remove category", {
                    ns: "Categories",
                  }),
                  handleClick: async () => {
                    await runClear(async () => {
                      await handleClearCategory(handleClose);
                    });
                  },
                  buttonProps: {
                    loading: processingClear,
                    variant: "outlined",
                    disabled: processing,
                  },
                },
              ] as ModalActionButtonProps[])
            : []),
          {
            label: t("Select", { ns: "Global" }),
            handleClick: async () => {
              if (!selectedCategoryId) throw new Error("Category missing");
              await runSelect(async () => {
                const category = categories.find(
                  c => c.id === selectedCategoryId
                );
                if (!category) throw new Error("Category not found");
                await handleSelectCategory(category, handleClose);
              });
            },
            buttonProps: {
              loading: processingSelect,
              disabled: !selectedCategoryId || processing,
            },
          },
        ] as ModalActionButtonProps[]
      }
      handleClose={handleClose}
      isLoading={loading}
    >
      <CategorySearchList
        selectedCategoryId={selectedCategoryId}
        setSelectedCategoryId={setSelectedCategoryId}
        onCategoryClick={onCategoryClick}
        excludeCategoryIds={excludeCategoryIds}
        categories={categories}
      />
    </Modal>
  );
};

export const CategorySearchList = ({
  selectedCategoryId,
  setSelectedCategoryId,
  onCategoryClick,
  excludeCategoryIds,
  categories,
}: {
  selectedCategoryId: string | null;
  setSelectedCategoryId: React.Dispatch<React.SetStateAction<string | null>>;
  onCategoryClick?: (category: CategoryFragment) => void;
  excludeCategoryIds?: string[];
  categories: CategoryFragment[];
}) => {
  const { t } = useTranslate(["Categories", "Global"]);

  const [searchTerm, setSearchTerm] = React.useState<string>("");

  const filteredCategories = React.useMemo(
    () => filterCategories(categories, searchTerm),
    [categories, searchTerm]
  );

  const { categoriesTree, categoriesMap } = React.useMemo(
    () => buildCategoryTree(filteredCategories),
    [filteredCategories]
  );

  const [expandedItemIds, setExpandedItemIds] = useCategoriesExpandedItems(
    categoriesMap,
    selectedCategoryId ? [selectedCategoryId] : []
  );

  if (!categories.length) return null;

  return (
    <Stack direction="column" spacing={2}>
      <DebouncedSearchInput
        defaultValue={searchTerm}
        placeholder={t("Search", { ns: "Global" })}
        onChangeSearchTerm={setSearchTerm}
        autoFocus
      />
      <SimpleTreeView
        expansionTrigger="iconContainer"
        multiSelect={false}
        selectedItems={selectedCategoryId}
        onItemSelectionToggle={(e, itemId, isSelected) => {
          setSelectedCategoryId(isSelected ? itemId : null);
        }}
        expandedItems={expandedItemIds}
        onItemExpansionToggle={(e, itemId, isExpanded) => {
          if (isExpanded) {
            setExpandedItemIds(ids => [...ids, itemId]);
          } else {
            setExpandedItemIds(ids => ids.filter(id => id !== itemId));
          }
        }}
      >
        <CategoryTreeItems
          categories={categoriesTree}
          onCategoryClick={onCategoryClick}
          excludeCategoryIds={excludeCategoryIds}
        />
      </SimpleTreeView>
    </Stack>
  );
};
